import React from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { useQuery } from 'react-query';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  Stack,
} from "@mui/material";

import { StripeProvider } from '../../../features/StripeCardForm';
import { UsersService } from '../../../services';
import { useWelcomeViewContext } from '../../../contexts/WelcomeViewContext';
import { useUserContext } from '../../../contexts/UserContext/context';
import { User } from '../../../models/User';
import GTMService from 'src/services/GTMService';

const SUBSCRIPTION_POLL_INTERVAL_MS = 2000;
const SUBSCRIPTION_POLL_MAX_ATTEMPTS = 10;

function PaymentCompleteInternal() {
  const history = useHistory();
  const stripe = useStripe();
  const [, dispatchUserAction ] = useUserContext();
  const { toggleWelcomeView } = useWelcomeViewContext();
  const [ message, setMessage ] = React.useState('');
  const [isResolvingPayment, setIsResolvingPayment] = React.useState(false);
  const [hasFatalError, setHasFatalError] = React.useState(false);
  const handledErrorRef = React.useRef(false);
  const purchasePushedRef = React.useRef(false);

  const params = React.useMemo(() => {
    return new URLSearchParams(history.location.search)
  }, [history.location.search]);

  const isCanceled = params.has('canceled');

  const query = useQuery({
    queryKey: ['payment-result'],
    enabled: !isCanceled,
    queryFn: async () => {
      const user = await UsersService.getMe();
      const status = user.subscription?.status;
      const isActive = status === 'active' || status === 'trialing';
      if (isActive) {
        return user;
      }
      throw new Error(`user.subscription.not_active`);
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    cacheTime: 0,
  });

  const handlePaymentSucceeded = React.useCallback(async (user: User, showWelcome: boolean, paymentType?: string) => {
    const isEligibleForCoupons = await UsersService
      .couponEligibility()
      .catch(() => user.coupon == null);

    if (!purchasePushedRef.current) {
      purchasePushedRef.current = true;
      const product = user.subscription?.subscriptionProduct;
      if (product) {
        GTMService.pushPurchase({
          product,
          user,
          coupon: user.coupon?.code,
          transactionId: params.get('payment_intent') ?? params.get('session_id') ?? undefined,
          customerType: showWelcome ? 'new' : 'returning',
          paymentType,
          effectivePrice: user.subscription?.currentPrice,
        });
      }
    }

    if (showWelcome) {
      dispatchUserAction({ type: 'logInSuccess', payload: { ...user, isEligibleForCoupons } });
      toggleWelcomeView(true);
      history.push(`/welcome`);
    } else {
      dispatchUserAction({ type: 'triggerManualPaymentSuccess', payload: { ...user, isEligibleForCoupons } });
      history.push(`/me/payments`);
    }
  }, [dispatchUserAction, history, toggleWelcomeView, params]);

  const pollForActiveSubscription = React.useCallback(async () => {
    for (let attempt = 0; attempt < SUBSCRIPTION_POLL_MAX_ATTEMPTS; attempt += 1) {
      const user = await UsersService.getMe();
      const status = user.subscription?.status;
      if (status === 'active' || status === 'trialing') {
        return user;
      }
      await new Promise((resolve) => setTimeout(resolve, SUBSCRIPTION_POLL_INTERVAL_MS));
    }
    throw new Error('payment.confirmation.timeout');
  }, []);

  const handleCheckoutSessionReturn = React.useCallback(async () => {
    try {
      setIsResolvingPayment(true);
      setHasFatalError(false);
      const user = await pollForActiveSubscription();
      await handlePaymentSucceeded(user, params.has('showWelcome'));
    } catch {
      setHasFatalError(true);
    } finally {
      setIsResolvingPayment(false);
    }
  }, [pollForActiveSubscription, handlePaymentSucceeded, params]);

  const handlePayment = React.useCallback(async (clientSecret: string) => {
    if (!stripe) {
      return;
    }
    try {
      setIsResolvingPayment(true);
      setHasFatalError(false);
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

      switch (paymentIntent?.status) {
        case 'succeeded': {
          await UsersService.confirmPaymentIntent(paymentIntent.id);
          const user = await pollForActiveSubscription();
          const paymentType = paymentIntent.payment_method_types?.[0]?.toUpperCase();
          await handlePaymentSucceeded(user, params.has('showWelcome'), paymentType);
          break;
        }
        case 'processing':
          setMessage('Twoja płatność jest przetwarzana.');
          break;
        case 'requires_payment_method':
          setHasFatalError(true);
          setMessage('Płatność się nie powiodła. Spróbuj ponownie.');
          break;
        default:
          setHasFatalError(true);
          setMessage('Wystąpił nieoczekiwany błąd.');
          break;
      }
    } catch {
      setHasFatalError(true);
    } finally {
      setIsResolvingPayment(false);
    }
  }, [stripe, pollForActiveSubscription, handlePaymentSucceeded, params]);

  React.useEffect(() => {
    if (query.status === 'success') {
      void handlePaymentSucceeded(query.data, params.has('showWelcome'));
    } else if (query.status === 'error' && !handledErrorRef.current) {
      handledErrorRef.current = true;
      const clientSecret = params.get('payment_intent_client_secret');
      const sessionId = params.get('session_id');
      if (clientSecret != null) {
        void handlePayment(clientSecret);
      } else if (sessionId != null) {
        void handleCheckoutSessionReturn();
      } else {
        setHasFatalError(true);
      }
    }
  }, [query.status, query.data, params, handlePayment, handleCheckoutSessionReturn, handlePaymentSucceeded]);

  if (isCanceled) {
    return (
      <Centered>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h5">Płatność anulowana</Typography>
          <Button component={Link} to="/me/payments" variant="outlined">
            Wróć do profilu
          </Button>
        </Stack>
      </Centered>
    );
  }

  if (query.status === 'loading' || isResolvingPayment) {
    return (
      <Centered>
        <Stack spacing={3} alignItems="center">
          <CircularProgress />
          <Typography variant="h5">Finalizowanie płatności…</Typography>
          <Typography color="text.secondary" align="center" maxWidth={520}>
            Czekamy, aż potwierdzenie płatności dotrze na nasze serwery.
            Zazwyczaj zajmuje to kilka sekund.
          </Typography>
        </Stack>
      </Centered>
    );
  }

  if (query.status === 'error' && hasFatalError) {
    return (
      <Centered>
        <Stack spacing={3} alignItems="center">
          <Alert severity="warning" sx={{ maxWidth: 560 }}>
            Nie udało się potwierdzić płatności. Skontaktuj się z obsługą klienta.
          </Alert>
          <Button component={Link} to="/me/payments" variant="outlined">
            Wróć do profilu
          </Button>
        </Stack>
      </Centered>
    );
  }

  return (
    <div id="payment-message">{message}</div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "grid",
        placeItems: "center",
        px: 2,
      }}
    >
      {children}
    </Box>
  );
}

export function PaymentComplete() {
  return (
    <StripeProvider>
      <PaymentCompleteInternal />
    </StripeProvider>
  );
}
